import { DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Observable, take, tap } from 'rxjs';

import { EntityTypeConstants } from 'src/app/common/EntityTypeConstants';
import { PersonalizationHistory } from 'src/app/common/PersonalizationHome';
import { PersonalizationService } from 'src/app/services/personalization.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { CallApiService } from '../../services/api-action.service';
import { PERSONALIZATION_ENABLED_SETTING } from '../../services/user-settings.service';
import { SettingsPageStore } from './settings-page.store';

function buildPersonalizationForm(fb: FormBuilder) {
  return fb.group({ personalizationEnabled: [false] });
}

/**
 * #94: the profile-scoped consent for personalized suggestions, and the GDPR handles on what it
 * stores. Its own form and its own save button, because switching it OFF has to be confirmed (it
 * deletes the history) and that warning must not ride along on an unrelated save.
 */
@Component({
  selector: 'app-personalization-settings',
  imports: [ReactiveFormsModule, AsyncClickDirective, RouterModule, DatePipe],
  templateUrl: './personalization-settings.component.html',
})
export class PersonalizationSettingsComponent {
  private readonly store = inject(SettingsPageStore);
  private readonly personalizationService = inject(PersonalizationService);
  private readonly callApiService = inject(CallApiService);

  readonly form = buildPersonalizationForm(inject(FormBuilder));
  // #94 (GDPR Art. 15): the stored history, loaded on demand behind „Gespeicherte Daten
  // anzeigen" — never with the page, so nobody's search history sits on screen unasked.
  history: PersonalizationHistory | null = null;
  historyVisible = false;
  historyLoading = false;

  constructor() {
    this.store.patchOnRead(EntityTypeConstants.EntityTypeProfile, this.form, () =>
      this.form.patchValue({ personalizationEnabled: this.storedEnabled() }),
    );
  }

  /** A profile created before the reseed has no row yet, and the backend reads that as "false" — so does this. */
  private storedEnabled(): boolean {
    return this.store.value(EntityTypeConstants.EntityTypeProfile, PERSONALIZATION_ENABLED_SETTING)?.toLowerCase() === 'true';
  }

  /**
   * Writes the consent setting. Switching it OFF also deletes the stored history — the backend
   * does that inside the very same write (#651 decision 2), so this is not a reversible toggle
   * and must not be presented as one. Hence the confirm on the true→false write, and only on
   * that one: granting consent needs no warning, and re-saving an already off switch has no
   * history to lose.
   */
  save(): Observable<unknown> {
    const scope = EntityTypeConstants.EntityTypeProfile;
    const enabled = !!this.form.controls.personalizationEnabled.value;
    const turningOff = !enabled && this.storedEnabled();

    return this.store
      .save$(
        [this.store.row(scope, PERSONALIZATION_ENABLED_SETTING, enabled ? 'true' : 'false')],
        turningOff
          ? $localize`:@@settings.personalization.offConfirm:Wenn Sie persönliche Empfehlungen ausschalten, wird Ihr gespeicherter Verlauf – Suchbegriffe und angesehene Artikel – dabei gelöscht. Ausschalten?`
          : undefined,
      )
      .pipe(
        tap(() => {
          this.form.markAsPristine();
          this.store.reload(scope);
          if (this.historyVisible) {
            this.loadHistory();
          }
        }),
      );
  }

  /**
   * #94 (Art. 17): „forget what you know about me" — deletes the stored history and leaves the
   * switch ON, which is what makes it a separate action from switching off.
   */
  deleteHistory(): Observable<unknown> {
    return this.callApiService
      .confirmAndCallWithToasts$(
        this.personalizationService.deleteHistory(),
        $localize`:@@settings.personalization.deleteConfirm:Soll Ihr gespeicherter Verlauf – Suchbegriffe und angesehene Artikel – wirklich gelöscht werden?`,
        $localize`:@@settings.personalization.deleteSuccess:Ihr Verlauf wurde gelöscht.`,
        $localize`:@@settings.personalization.deleteError:Der Verlauf konnte nicht gelöscht werden.`,
      )
      .pipe(tap(() => (this.history = { personalizationEnabled: this.storedEnabled(), visits: [], searches: [] })));
  }

  /** #94 (Art. 15): shows what is actually stored, read only when the user asks for it. */
  toggleHistory(): void {
    this.historyVisible = !this.historyVisible;
    if (this.historyVisible && !this.history) {
      this.loadHistory();
    }
  }

  private loadHistory(): void {
    this.historyLoading = true;
    this.personalizationService
      .getHistory()
      .pipe(take(1))
      .subscribe({
        next: (history) => {
          this.history = history;
          this.historyLoading = false;
        },
        error: () => {
          this.history = null;
          this.historyLoading = false;
        },
      });
  }
}
